import React, { useEffect } from "react";
import { useForm, Controller, useFieldArray, set } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  TextField,
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  Button,
  IconButton,
  Divider,
} from "@mui/material";
import FormDialog from "@components/FormDialog";
import { useTranslation } from "react-i18next";
import { X, Plus } from "lucide-react";
import { useSnackbar } from "@context/SnackBarProvider";
import { useMutation } from "@tanstack/react-query";
import {
  updateDriver,
  UpdateDriverFormData,
  updateDriverSchema,
} from "@lib/api/mutations/driver";
import { AxiosError } from "axios";
import { Driver, DriverResponse } from "@lib/types/driver";
import useHandleError from "@lib/utils/errorHook";

interface Props {
  updateDriverDialogOpen: boolean;
  setUpdateDriverDialogOpen: (open: boolean) => void;
  refetch?: () => void;
  driverData?: Driver;
}

const UpdateDriverForm: React.FC<Props> = ({
  updateDriverDialogOpen,
  setUpdateDriverDialogOpen,
  refetch,
  driverData,
}) => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const handleError = useHandleError();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<UpdateDriverFormData>({
    resolver: zodResolver(updateDriverSchema),
    defaultValues: {
      name: driverData?.name || "",
      code: driverData?.code || "",
      description: driverData?.description || "",
      phone: driverData?.phone || "",
      mobile_key: driverData?.mobile_key || "",
      exclusive: driverData?.exclusive || false,
      custom_fields: driverData?.custom_fields || [],
    },
  });

  // Field Array for dynamic custom fields
  const { fields, append, remove } = useFieldArray({
    control,
    name: "custom_fields",
  });

  const { mutate: updateMutate, isPending: isUpdatingDriver } = useMutation({
    mutationFn: updateDriver,
    onSuccess: (data: DriverResponse) => {
      showSnackbar(t("user.drivers.updateSuccess"), "success");
      setUpdateDriverDialogOpen(false);
      reset();
      refetch && refetch();
    },
    onError: (error: AxiosError | unknown) => {
      if (error instanceof AxiosError && error.response) {
        const errorDetails = handleError(error.response.data);
        errorDetails && showSnackbar(errorDetails.message, "error");
      } else {
        showSnackbar(t("general.error"), "error");
      }
    },
  });

  const handleFormSubmit = (data: UpdateDriverFormData) => {
    if (driverData) {
      updateMutate({ updateDriverParams: data, driverId: driverData.id });
    } else {
      showSnackbar(t("general.error"), "error");
    }
  };

  useEffect(() => {
    if (driverData) {
      setValue("name", driverData.name);
      setValue("code", driverData.code);
      setValue("description", driverData.description);
      setValue("phone", driverData.phone);
      setValue("mobile_key", driverData.mobile_key);
      setValue("exclusive", driverData.exclusive);
      setValue("custom_fields", driverData.custom_fields);
    }
  }, [driverData, updateDriverDialogOpen]);

  return (
    <FormDialog
      title={t("user.drivers.edit")}
      open={updateDriverDialogOpen}
      onClose={() => {
        setUpdateDriverDialogOpen(false);
        reset();
      }}
      onSubmit={handleSubmit(handleFormSubmit)}
      submitText={t("general.save")}
      isSubmitting={isUpdatingDriver}
      submittingText={t("general.saving")}
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
        <Box display="flex" flexDirection="column" gap={2} py={2}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("user.drivers.name")}
                variant="outlined"
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />
          <Controller
            name="code"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("user.drivers.code")}
                variant="outlined"
                fullWidth
                error={!!errors.code}
                helperText={errors.code?.message}
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("user.drivers.description")}
                variant="outlined"
                fullWidth
                error={!!errors.description}
                helperText={errors.description?.message}
              />
            )}
          />
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("user.drivers.phone")}
                variant="outlined"
                fullWidth
                error={!!errors.phone}
                helperText={errors.phone?.message}
              />
            )}
          />
          <Controller
            name="mobile_key"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("user.drivers.mobileKey")}
                variant="outlined"
                fullWidth
                error={!!errors.mobile_key}
                helperText={errors.mobile_key?.message}
              />
            )}
          />

          <TextField
            label={t("user.drivers.parameterNumber")}
            variant="outlined"
            value={driverData?.parameter_number}
            disabled={true}
          />
          <Controller
            name="exclusive"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                label="Exclusive"
                control={
                  <Checkbox
                    {...field}
                    className="hover:bg-transparent"
                    checked={field.value}
                    onChange={(event) => field.onChange(event.target.checked)}
                  />
                }
                className="text-sm"
              />
            )}
          />
          <Divider />
          {/* Custom Fields Section */}
          <Box>
            <Typography variant="subtitle1">
              {t("user.drivers.customFields")}
            </Typography>
            {fields.map((item, index) => (
              <Box
                key={item.id}
                display="flex"
                gap={2}
                alignItems="center"
                my={1}
              >
                <Controller
                  name={`custom_fields.${index}.key`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t("user.drivers.customFieldKey")}
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
                <Controller
                  name={`custom_fields.${index}.value`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t("user.drivers.customFieldValue")}
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />

                <IconButton onClick={() => remove(index)} color="error">
                  <X />
                </IconButton>
              </Box>
            ))}
            <Button
              variant="outlined"
              onClick={() => append({ key: "", value: "" })}
              startIcon={<Plus />}
            >
              {t("user.drivers.addCustomField")}
            </Button>
          </Box>
        </Box>
      </form>
    </FormDialog>
  );
};

export default UpdateDriverForm;
